/**
 * Student Dashboard — Soorgaa Design System
 * 
 * Information Architecture:
 * 1. Greeting + Primary CTA
 * 2. Continue Exam (if uncompleted session exists)
 * 3. Quick Actions Grid
 * 4. Recent Course Materials
 * 5. Practice Activity Statistics
 * 6. Academic Metrics Overview
 * 7. Upgrade Banner
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  BookOpen,
  Brain,
  Trophy,
  Star,
  Sparkles,
  Loader2,
  Download,
  Play,
  Award,
  Calculator,
  FileText,
} from "lucide-react";
import { SubjectBadge } from "../../components/ui/Badges";
import { QuickActionGrid, type QuickActionItem } from "../../components/ui/QuickActionGrid";
import { Hero, HeroSkeleton } from "../../components/ui/Hero";
import { Card, CardHeader } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { ListRow } from "../../components/ui/ListRow";
import { CourseCard } from "../../components/ui/CourseCard";
import { useStudyProgressState } from "../../lib/useStudyProgress";
import { listUnfinishedQuizChapters, userIdFromToken } from "../../lib/offlineChapters";
import { Skeleton, SkeletonCard } from "../../components/ui/Skeleton";
import { toast } from "../../components/ui/Toast";
import { InstallBanner } from "../../components/pwa/InstallBanner";
import { useStudent } from "../../context/StudentContext";
import { useAuth } from "../../context/AuthContext";
import { api, ApiError, type StudentResource } from "../../lib/api";
import { saveBlobAsFile, safeDownloadName } from "../../lib/download";
import { clearCachedSession, listCachedSessions, saveQuizSession } from "../../lib/quizSession";
import { StatusBanner } from "../../components/ui/StatusBanner";
import { StatusMessage, mapApiError, type MappedApiError } from "../../components/ui/StatusMessage";
import { saveRedirectTarget } from "../../lib/redirect";
import { formatDisplayTitle } from "../../lib/formatTitle";
import { FeedbackModal } from "../../components/student/FeedbackModal";

export function StudentDashboard({ previewUserId }: { previewUserId?: string } = {}) {
  const { openUpgrade } = useStudent();
  const { tokens } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const token = tokens?.accessToken ?? null;
  const [planLabel, setPlanLabel] = useState("99 ETB/mo");

  const [data, setData] = useState<Awaited<ReturnType<typeof api.student.dashboard>> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<MappedApiError | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [forceUpdate, setForceUpdate] = useState(0);
  const [softDeletedIds, setSoftDeletedIds] = useState<Set<string>>(new Set());
  const [feedbackPrompt, setFeedbackPrompt] = useState<{
    isOpen: boolean;
    reason?: string | null;
    title?: string;
    message?: string;
  }>({ isOpen: false });

  // In-progress exams only: must be an exam and must not be fully completed or soft-deleted
  const inProgressExams = useMemo(() => {
    return listCachedSessions().filter((item) => {
      if (item.type !== "exam") return false;
      if (softDeletedIds.has(item.id)) return false;
      const total = item.session?.questions?.length ?? 0;
      if (total <= 0) return false;
      const answered = item.answers?.filter((a) => a != null && a >= 0).length ?? 0;
      return answered < total;
    });
  }, [forceUpdate, softDeletedIds]);

  // Fallbacks for the hero: an unfinished chapter quiz on this device, then the last chapter read.
  const { data: studyProgress, loading: progressLoading } = useStudyProgressState();
  const [quizResume, setQuizResume] = useState<{ chapterId: string; title: string; slug: string } | null>(null);
  // True once the unfinished-quiz lookup has finished, so the "continue" area never appears late.
  const [resumeChecked, setResumeChecked] = useState(false);
  const [premiumUntil, setPremiumUntil] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    if (!token) return;
    api.profile(token).then((p) => setPremiumUntil(p.premiumUntil ?? null)).catch(() => setPremiumUntil(null));
  }, [token]);
  useEffect(() => {
    const uid = userIdFromToken(token);
    if (progressLoading) return;
    if (!token || !uid || !studyProgress) {
      setResumeChecked(true);
      return;
    }
    let cancelled = false;
    (async () => {
      const ids = await listUnfinishedQuizChapters(uid).catch(() => []);
      for (const id of ids) {
        try {
          const d = await api.student.chapters.detail(token, id);
          const slug = studyProgress.courses.find((c) => c.course_id === d.course_id)?.slug;
          if (slug && !cancelled) {
            setQuizResume({ chapterId: id, title: d.title, slug });
            return;
          }
        } catch {
          /* chapter no longer available: try the next one */
        }
      }
    })().finally(() => {
      if (!cancelled) setResumeChecked(true);
    });
    return () => {
      cancelled = true;
    };
  }, [token, studyProgress, progressLoading]);

  const loadDashboard = useCallback(() => {
    if (!token) return;
    setLoading(true);
    setError(null);
    api.student.dashboard(token, previewUserId)
      .then((res) => {
        setData(res);
        setError(null);
      })
      .catch((e) => {
        const mapped = mapApiError(e);
        if (mapped.isSessionExpired) {
          saveRedirectTarget(location.pathname);
          navigate(`/auth/login?redirect=${encodeURIComponent(location.pathname)}`);
          return;
        }
        setError(mapped);
      })
      .finally(() => setLoading(false));

    api.payments.packages().then((pkgs) => {
      const lowest = pkgs[0];
      if (lowest) setPlanLabel(`${lowest.price} ETB`);
    }).catch(() => {});

    // Check for delayed or premium rating prompt
    api.feedback.promptStatus(token)
      .then((status) => {
        if (status.showPrompt) {
          setFeedbackPrompt({
            isOpen: true,
            reason: status.reason,
            title: status.title,
            message: status.message,
          });
        }
      })
      .catch(() => {});
  }, [token, previewUserId, location.pathname, navigate]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  function handleDiscardExam(itemId: string) {
    setSoftDeletedIds((prev) => new Set(prev).add(itemId));

    // Permanently remove after 8 seconds unless undone
    const timer = setTimeout(() => {
      clearCachedSession(itemId);
      setForceUpdate((v) => v + 1);
    }, 8000);

    toast.info("Exam session removed.", {
      duration: 8000,
      action: {
        label: "Undo",
        onClick: () => {
          clearTimeout(timer);
          setSoftDeletedIds((prev) => {
            const next = new Set(prev);
            next.delete(itemId);
            return next;
          });
          toast.success("Exam session restored.");
        },
      },
    });
  }

  async function handleDownload(r: StudentResource) {
    if (r.locked) {
      openUpgrade();
      return;
    }
    if (!token) return;
    try {
      setDownloadingId(r.id);
      const blob = await api.student.modules.downloadBlob(token, r.id);
      saveBlobAsFile(blob, safeDownloadName(r.title, r.fileType ?? "pdf"));
      toast.success(`Downloaded ${r.title}`);
    } catch (e) {
      const mapped = mapApiError(e);
      toast.error(mapped.message);
    } finally {
      setDownloadingId(null);
    }
  }

  const quickActions: QuickActionItem[] = [
    {
      icon: <BookOpen size={20} />,
      label: "Course Modules",
      description: "Official freshman modules & short revision notes",
      actionText: "Open catalog",
      onClick: () => navigate("/student/study"),
    },
    {
      icon: <Brain size={20} />,
      label: "Chapter Quizzes",
      description: "Fast diagnostic quizzes with instant feedback",
      actionText: "Take a quiz",
      onClick: () => navigate("/student/quiz"),
    },
    {
      icon: <Trophy size={20} />,
      label: "Past Exams",
      description: "Timed university midterms and finals archive",
      actionText: "Practice exam",
      onClick: () => navigate("/student/exams"),
    },
    {
      icon: <Award size={20} />,
      label: "COC Practice",
      description: "National exit & competency exam simulations",
      actionText: "Start COC",
      onClick: () => navigate("/student/coc"),
    },
  ];

  if (loading) {
    return (
      <div className="space-y-6 max-w-7xl" aria-busy="true" aria-label="Loading dashboard">
        <HeroSkeleton />
        <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonCard key={i} lines={2} className="h-40" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <StatusMessage
        variant={error?.variant ?? "error"}
        title={error?.title ?? "We could not load your dashboard"}
        message={error?.message ?? "Please check your connection and try again."}
        requestId={error?.requestId}
        action={{
          label: "Retry",
          onClick: loadDashboard,
        }}
      />
    );
  }

  return (
    <div className="space-y-6 max-w-7xl">
      {/* 1. Hero: the four stats that matter */}
      {(() => {
        const total = (studyProgress?.courses ?? []).reduce((n, c) => n + c.total, 0);
        const done = (studyProgress?.courses ?? []).reduce((n, c) => n + c.done, 0);
        const daysLeft =
          premiumUntil ? Math.max(0, Math.ceil((new Date(premiumUntil).getTime() - Date.now()) / 86_400_000)) : null;
        return (
          <Hero
            title={data.greeting}
            subtitle={data.subtitle}
            actions={
              <Button to="/student/study" variant="primary" size="sm" trailingArrow>
                Start studying
              </Button>
            }
            stats={[
              {
                label: "Chapters",
                value: total > 0 ? `${done}/${total}` : "0",
                hint: "Finished chapters",
                progress: total > 0 ? (done / total) * 100 : 0,
                loading: progressLoading,
              },
              {
                label: "Quizzes",
                value: data.activityStats?.quizzesCompleted ?? 0,
                icon: <Brain size={18} />,
                hint:
                  data.activityStats && data.activityStats.quizzesStarted > data.activityStats.quizzesCompleted
                    ? `${data.activityStats.quizzesStarted} started`
                    : undefined,
              },
              {
                label: "Exams",
                value: data.activityStats?.examsCompleted ?? 0,
                icon: <Trophy size={18} />,
              },
              {
                label: "Plan",
                value: data.isPremium ? "Premium" : "Free",
                icon: <Star size={18} />,
                hint: data.isPremium ? (daysLeft != null ? `${daysLeft} days left` : premiumUntil === undefined ? undefined : "Active") : "Tap to upgrade",
                loading: data.isPremium && premiumUntil === undefined,
                onClick: data.isPremium ? undefined : () => openUpgrade(),
              },
            ]}
          />
        );
      })()}

      {/* PWA Install Banner (dismissible, appears after 2nd visit) */}
      <InstallBanner />

      {/* 2. Hero "Continue": the most recent unfinished exam */}
      {inProgressExams.length > 0 && (() => {
        const [hero, ...others] = inProgressExams;
        const answered = hero.answers?.filter((a) => a != null && a >= 0).length ?? 0;
        const total = hero.session?.questions?.length ?? 1;
        const pct = Math.round((answered / total) * 100);
        const resume = (item: typeof hero) => {
          if (item.session) {
            saveQuizSession(item.session);
            navigate("/student/study/quiz/play");
          }
        };
        return (
          <section aria-labelledby="continue-heading" className="space-y-3">
            <div className="rounded-card border border-border bg-card p-5 space-y-4 shadow-soft">
              <div className="flex items-center justify-between gap-2">
                <h3 id="continue-heading" className="text-sm font-semibold text-muted-foreground">Continue where you left off</h3>
                <SubjectBadge subject={hero.subject} />
              </div>
              <div className="space-y-1">
                <p className="text-xl font-bold font-display line-clamp-2">{formatDisplayTitle(hero.title)}</p>
                <p className="text-sm text-muted-foreground">Question {(hero.currentQuestion ?? 0) + 1} of {total}</p>
              </div>
              <div className="h-1.5 w-full rounded-full bg-track" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label="Exam progress">
                <div className="h-full rounded-full bg-primary transition-all duration-300" style={{ width: `${Math.max(5, pct)}%` }} />
              </div>
              <div className="flex items-center gap-2">
                <Button type="button" size="lg" icon={<Play size={16} />} onClick={() => resume(hero)} className="flex-1 sm:flex-none">
                  Resume exam
                </Button>
                <Button type="button" variant="ghost" onClick={() => handleDiscardExam(hero.id)} aria-label="Dismiss this exam">
                  Dismiss
                </Button>
              </div>
            </div>
            {others.length > 0 && (
              <ul className="space-y-2">
                {others.map((item) => (
                  <li key={item.id}>
                    <ListRow
                      icon={<Play size={16} />}
                      title={formatDisplayTitle(item.title)}
                      subtitle={`Question ${(item.currentQuestion ?? 0) + 1} of ${item.session?.questions?.length ?? 1}`}
                      action={
                        <span className="flex gap-1">
                          <Button type="button" size="sm" variant="secondary" onClick={() => resume(item)}>Resume</Button>
                          <Button type="button" size="sm" variant="ghost" onClick={() => handleDiscardExam(item.id)} aria-label="Dismiss this exam">Dismiss</Button>
                        </span>
                      }
                    />
                  </li>
                ))}
              </ul>
            )}
          </section>
        );
      })()}

      {inProgressExams.length === 0 && (progressLoading || !resumeChecked) && <Skeleton className="h-40 w-full rounded-card" />}

      {inProgressExams.length === 0 && resumeChecked && (quizResume || studyProgress?.last_read) && (
        <div className="rounded-card border border-border bg-card p-5 space-y-3 shadow-soft">
          <h3 className="text-sm font-semibold text-muted-foreground">Continue where you left off</h3>
          {quizResume ? (
            <>
              <p className="text-xl font-bold font-display line-clamp-2">Quiz: {quizResume.title}</p>
              <Button to={`/student/study/${quizResume.slug}/chapters/${quizResume.chapterId}?tab=quiz`} size="lg" trailingArrow>
                Resume quiz
              </Button>
            </>
          ) : (
            studyProgress?.last_read && (
              <>
                <p className="text-xl font-bold font-display line-clamp-2">{studyProgress.last_read.chapter_title}</p>
                <Button
                  to={`/student/study/${studyProgress.last_read.course_slug}/chapters/${studyProgress.last_read.chapter_id}/notes?slide=${studyProgress.last_read.slide_id}`}
                  size="lg"
                  trailingArrow
                >
                  Continue reading
                </Button>
              </>
            )
          )}
        </div>
      )}

      {!progressLoading && (() => {
        const active = (studyProgress?.courses ?? []).filter((c) => c.done > 0 && c.done < c.total);
        return active.length > 0 ? (
          <section aria-labelledby="in-progress-courses" className="space-y-3">
            <h3 id="in-progress-courses" className="text-section font-bold font-display text-foreground">Courses in progress</h3>
            <div className="grid grid-cols-[repeat(auto-fill,minmax(16.25rem,1fr))] gap-4">
              {active.map((c) => (
                <CourseCard
                  key={c.course_id}
                  to={`/student/study/${c.slug}`}
                  title={c.name}
                  subtitle={c.code}
                  icon={<BookOpen size={20} />}
                  progress={{ label: "Chapters completed", done: c.done, total: c.total }}
                />
              ))}
            </div>
          </section>
        ) : null;
      })()}

      {/* New student: nothing started yet */}
      {inProgressExams.length === 0 && resumeChecked && !progressLoading && !quizResume && !studyProgress?.last_read && !data.recentResources?.length && !(data.activityStats?.quizzesCompleted) && (
        <div className="rounded-card border border-border bg-card p-6 text-center space-y-3">
          <p className="text-lg font-semibold font-display">Pick a semester to start</p>
          <Button to="/student/study" trailingArrow>Open Study Hub</Button>
        </div>
      )}

      {/* 3. Quick Actions Grid */}
      <section aria-labelledby="quick-actions-heading">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Sparkles size={16} className="text-primary" />
            <h3 id="quick-actions-heading" className="font-bold text-sm sm:text-base text-foreground tracking-tight">
              Quick actions
            </h3>
          </div>
          <span className="text-xs text-muted-foreground hidden sm:inline">
            Interactive workspaces
          </span>
        </div>
        <QuickActionGrid items={quickActions} />
      </section>

      {/* 4. Recent Course Materials */}
      {data.recentResources && data.recentResources.length > 0 && (
        <section aria-labelledby="recent-materials-heading" className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h3 id="recent-materials-heading" className="text-section font-bold font-display text-foreground">
              Recent materials
            </h3>
            <Link to="/student/study" className="inline-flex min-h-11 items-center text-sm text-accent-foreground font-semibold hover:underline">
              Browse all materials &rarr;
            </Link>
          </div>

          <div className="space-y-2">
            {data.recentResources.slice(0, 3).map((r) => (
              <ListRow
                key={r.id}
                icon={<FileText size={18} />}
                title={formatDisplayTitle(r.title)}
                subtitle={[r.subject, r.pages && r.pages > 0 ? `${r.pages} pages` : null].filter(Boolean).join(" · ")}
                action={
                  <Button
                    type="button"
                    variant={r.locked ? "primary" : "secondary"}
                    size="sm"
                    trailingArrow={!r.locked && downloadingId !== r.id}
                    onClick={() => handleDownload(r)}
                    disabled={downloadingId === r.id}
                    icon={downloadingId === r.id ? <Loader2 size={14} className="animate-spin" /> : undefined}
                  >
                    {r.locked ? "Upgrade to unlock" : downloadingId === r.id ? "Downloading…" : "Download"}
                  </Button>
                }
              />
            ))}
          </div>
        </section>
      )}

      {/* 7. Upgrade Banner for Free Users */}
      {!data.isPremium && (
        <Card
          variant="interactive"
          onClick={() => openUpgrade()}
          className="border-l-2 border-l-primary flex items-center justify-between gap-4"
        >
          <div>
            <p className="text-xs font-semibold text-primary uppercase tracking-wider mb-0.5">Free plan</p>
            <h3 className="text-base sm:text-lg font-bold text-foreground">Unlock full past exam archives</h3>
            <span className="inline-flex items-center gap-1.5 text-primary text-xs font-bold mt-1.5">
              <Star size={13} /> Upgrade for {planLabel} &rarr;
            </span>
          </div>
          <Sparkles size={28} className="text-primary shrink-0 opacity-80" />
        </Card>
      )}

      {/* Rating Prompt */}
      <FeedbackModal
        isOpen={feedbackPrompt.isOpen}
        onClose={() => setFeedbackPrompt({ isOpen: false })}
        triggerType={feedbackPrompt.reason === "premium_7d" ? "premium" : "delayed"}
        title={feedbackPrompt.title || "How are you finding Soorgaa?"}
        subtitle={feedbackPrompt.message || "We'd love to hear your feedback so we can continue improving your study experience."}
      />
    </div>
  );
}
